import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { getImageUrl } from '../../utils/imageUrl';
import api from '../../utils/api';
import Toast from '../../components/Toast';

function AdminDashboard() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [passeios, setPasseios] = useState([]);
  const [whatsappGlobal, setWhatsappGlobal] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('passeios');
  const [showModal, setShowModal] = useState(false);
  const [editingPasseio, setEditingPasseio] = useState(null);
  const [formData, setFormData] = useState({
    nome_pt: '',
    nome_en: '',
    nome_es: '',
    descricao_pt: '',
    descricao_en: '',
    descricao_es: '',
    localizacao_pt: '',
    localizacao_en: '',
    localizacao_es: '',
    duracao: '',
    whatsapp: '',
    capacidade_maxima: 50,
    disponivel: true
  });
  const [imagens, setImagens] = useState([]);
  const [imagensExistentes, setImagensExistentes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 'passeios') {
        const response = await api.get('/passeios/admin/all');
        setPasseios(response.data);
      }
    } catch (err) {
      console.error('Erro ao buscar dados:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  const fetchWhatsappGlobal = useCallback(async () => {
    try {
      const response = await api.get('/configuracoes/admin/all');
      setWhatsappGlobal(response.data.whatsapp_numero || '');
    } catch (err) {
      console.error('Erro ao buscar configurações:', err);
    }
  }, []);

  useEffect(() => {
    fetchData();
    if (activeTab === 'configuracoes') {
      fetchWhatsappGlobal();
    }
  }, [activeTab, fetchData, fetchWhatsappGlobal]);


  const handleSaveWhatsapp = async () => {
    try {
      await api.put('/configuracoes/whatsapp', { whatsapp: whatsappGlobal });
      setToast({ message: 'Número WhatsApp atualizado com sucesso!', type: 'success' });
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Erro ao salvar número WhatsApp', type: 'error' });
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const handleOpenModal = (passeio = null) => {
    if (passeio) {
      setEditingPasseio(passeio.id);
      setFormData({
        nome_pt: passeio.nome_pt || passeio.nome || '',
        nome_en: passeio.nome_en || '',
        nome_es: passeio.nome_es || '',
        descricao_pt: passeio.descricao_pt || passeio.descricao || '',
        descricao_en: passeio.descricao_en || '',
        descricao_es: passeio.descricao_es || '',
        localizacao_pt: passeio.localizacao_pt || passeio.localizacao || '',
        localizacao_en: passeio.localizacao_en || '',
        localizacao_es: passeio.localizacao_es || '',
        duracao: passeio.duracao || '',
        whatsapp: passeio.whatsapp || '',
        capacidade_maxima: passeio.capacidade_maxima || 50,
        disponivel: passeio.disponivel !== undefined ? passeio.disponivel : true
      });
      setImagensExistentes(passeio.Imagens || []);
    } else {
      setEditingPasseio(null);
      setFormData({
        nome_pt: '',
        nome_en: '',
        nome_es: '',
        descricao_pt: '',
        descricao_en: '',
        descricao_es: '',
        localizacao_pt: '',
        localizacao_en: '',
        localizacao_es: '',
        duracao: '',
        whatsapp: '',
        capacidade_maxima: 50,
        disponivel: true
      });
      setImagensExistentes([]);
    }
    setImagens([]);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPasseio(null);
    setSaving(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const maxSize = 2 * 1024 * 1024; // 2MB em bytes
    
    // Validar tamanho de cada arquivo ANTES de adicionar à fila
    const invalidFiles = [];
    
    // Verificar TODOS os arquivos primeiro
    files.forEach(file => {
      if (file.size > maxSize) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
        invalidFiles.push(`${file.name} (${sizeMB}MB)`);
      }
    });
    
    // Se houver QUALQUER arquivo inválido, rejeitar TODOS e não adicionar nenhum à fila
    if (invalidFiles.length > 0) {
      const message = (
        <div>
          <p className="font-semibold mb-2">Arquivos muito grandes (máximo: 2MB)</p>
          <ul className="list-disc list-inside space-y-1 text-sm">
            {invalidFiles.map((file, index) => (
              <li key={index}>{file}</li>
            ))}
          </ul>
          <p className="mt-2 text-sm">Por favor, selecione apenas arquivos menores que 2MB.</p>
        </div>
      );
      setToast({ message, type: 'error', duration: 8000 });
      // Limpar o input para permitir nova seleção
      e.target.value = '';
      return;
    }
    
    // Se todos os arquivos são válidos, adicionar à fila
    setImagens(prevImagens => [...prevImagens, ...files]);
    
    // Limpar o input para permitir nova seleção
    e.target.value = '';
  };

  const handleRemoveExistingImage = async (imagemId) => {
    if (!editingPasseio) {
      // Se não está editando um passeio, apenas remove da lista local
      setImagensExistentes(imagensExistentes.filter(img => img.id !== imagemId));
      return;
    }

    // Confirmar antes de deletar
    if (!window.confirm('Tem certeza que deseja remover esta imagem?')) {
      return;
    }

    try {
      // Deletar imediatamente via API
      await api.delete(`/passeios/${editingPasseio}/imagens/${imagemId}`);
      
      // Remover da lista local
      setImagensExistentes(imagensExistentes.filter(img => img.id !== imagemId));
      
      // Atualizar a lista de passeios para refletir a mudança
      fetchData();
      setToast({ message: 'Imagem removida com sucesso!', type: 'success' });
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Erro ao remover imagem', type: 'error' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const formDataToSend = new FormData();
      
      // Adicionar campos do formulário
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });

      // Adicionar imagens
      imagens.forEach((file) => {
        formDataToSend.append('imagens', file);
      });

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      };

      if (editingPasseio) {
        await api.put(`/passeios/${editingPasseio}`, formDataToSend, config);
      } else {
        await api.post('/passeios', formDataToSend, config);
      }
      handleCloseModal();
      fetchData();
      setToast({ message: editingPasseio ? 'Passeio atualizado com sucesso!' : 'Passeio criado com sucesso!', type: 'success' });
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Erro ao salvar passeio', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('admin.confirmDelete'))) {
      try {
        await api.delete(`/passeios/${id}`);
        fetchData();
        setToast({ message: 'Passeio excluído com sucesso!', type: 'success' });
      } catch (err) {
        setToast({ message: err.response?.data?.error || 'Erro ao excluir passeio', type: 'error' });
      }
    }
  };


  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-blue-600 text-white shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">{t('admin.title')}</h1>
              <p className="text-blue-100">Bem-vindo, {user?.username}</p>
            </div>
            <div className="flex gap-4">
              <a
                href="/"
                className="bg-white text-blue-600 px-4 py-2 rounded-lg font-semibold hover:bg-blue-50 transition"
              >
                Ver Site
              </a>
              <button
                onClick={handleLogout}
                className="bg-red-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-red-600 transition"
              >
                {t('admin.logout')}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white shadow-md">
        <div className="container mx-auto px-4">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('passeios')}
              className={`px-6 py-4 font-semibold border-b-2 transition ${
                activeTab === 'passeios'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-blue-600'
              }`}
            >
              {t('admin.tours')}
            </button>
            <button
              onClick={() => setActiveTab('configuracoes')}
              className={`px-6 py-4 font-semibold border-b-2 transition ${
                activeTab === 'configuracoes'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-blue-600'
              }`}
            >
              {t('admin.globalSettings')}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        {activeTab === 'passeios' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                Gerenciar Passeios
              </h2>
              <button
                onClick={() => handleOpenModal()}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition"
              >
                + {t('admin.addTour')}
              </button>
            </div>

            {loading ? (
              <div className="text-center py-12">{t('common.loading')}</div>
            ) : passeios.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                Nenhum passeio cadastrado.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {passeios.map((passeio) => (
                  <div
                    key={passeio.id}
                    className="bg-white rounded-lg shadow-md overflow-hidden"
                  >
                    {passeio.Imagens && passeio.Imagens.length > 0 ? (
                      <img
                        src={getImageUrl(passeio.Imagens[0].imagem_url)}
                        alt={passeio.nome}
                        className="w-full h-48 object-cover object-center"
                      />
                    ) : passeio.imagem_url ? (
                      <img
                        src={getImageUrl(passeio.imagem_url)}
                        alt={passeio.nome}
                        className="w-full h-48 object-cover object-center"
                      />
                    ) : (
                      <div className="w-full h-48 bg-gradient-to-br from-blue-400 to-blue-600"></div>
                    )}
                    <div className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="text-lg font-bold text-gray-800">
                          {passeio.nome}
                        </h3>
                        <span
                          className={`px-2 py-1 rounded text-xs ${
                            passeio.disponivel
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {passeio.disponivel ? t('tours.available') : t('tours.unavailable')}
                        </span>
                      </div>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {passeio.descricao}
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleOpenModal(passeio)}
                          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded font-semibold hover:bg-blue-700 transition text-sm"
                        >
                          {t('admin.editTour')}
                        </button>
                        <button
                          onClick={() => handleDelete(passeio.id)}
                          className="flex-1 bg-red-600 text-white px-4 py-2 rounded font-semibold hover:bg-red-700 transition text-sm"
                        >
                          {t('admin.deleteTour')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'configuracoes' && (
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              {t('admin.globalSettings')}
            </h2>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">
                {t('admin.whatsappNumber')}
              </h3>
              <p className="text-gray-600 mb-4">
                Este número será usado como padrão quando um passeio não tiver um número específico configurado.
              </p>
              
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <label className="block text-gray-700 font-semibold mb-2">
                    Número WhatsApp (apenas números, ex: 5511999999999)
                  </label>
                  <input
                    type="text"
                    value={whatsappGlobal}
                    onChange={(e) => setWhatsappGlobal(e.target.value)}
                    placeholder="5511999999999"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-gray-500 text-sm mt-2">
                    Formato: código do país + DDD + número (sem espaços ou caracteres especiais)
                  </p>
                </div>
                  <button
                    onClick={handleSaveWhatsapp}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition whitespace-nowrap"
                  >
                    {t('admin.saveSettings')}
                  </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
            {saving && (
              <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
                <div className="text-center">
                  <svg className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <p className="text-gray-700 font-semibold">{editingPasseio ? 'Salvando passeio...' : 'Criando passeio...'}</p>
                </div>
              </div>
            )}
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">
                  {editingPasseio ? t('admin.editTour') : t('admin.addTour')}
                </h2>
                <button
                  onClick={handleCloseModal}
                  disabled={saving}
                  className="text-gray-500 hover:text-gray-700 text-2xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Nome - Multilíngue */}
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('admin.form.name')}</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">
                        {t('admin.form.namePt')} *
                      </label>
                      <input
                        type="text"
                        name="nome_pt"
                        value={formData.nome_pt}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">
                        {t('admin.form.nameEn')}
                      </label>
                      <input
                        type="text"
                        name="nome_en"
                        value={formData.nome_en}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">
                        {t('admin.form.nameEs')}
                      </label>
                      <input
                        type="text"
                        name="nome_es"
                        value={formData.nome_es}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                {/* Descrição - Multilíngue */}
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('admin.form.description')}</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">
                        {t('admin.form.descriptionPt')} *
                      </label>
                      <textarea
                        name="descricao_pt"
                        value={formData.descricao_pt}
                        onChange={handleChange}
                        required
                        rows="3"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">
                        {t('admin.form.descriptionEn')}
                      </label>
                      <textarea
                        name="descricao_en"
                        value={formData.descricao_en}
                        onChange={handleChange}
                        rows="3"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">
                        {t('admin.form.descriptionEs')}
                      </label>
                      <textarea
                        name="descricao_es"
                        value={formData.descricao_es}
                        onChange={handleChange}
                        rows="3"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                {/* Localização - Multilíngue */}
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('admin.form.location')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">
                        {t('admin.form.locationPt')} *
                      </label>
                      <input
                        type="text"
                        name="localizacao_pt"
                        value={formData.localizacao_pt}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">
                        {t('admin.form.locationEn')}
                      </label>
                      <input
                        type="text"
                        name="localizacao_en"
                        value={formData.localizacao_en}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">
                        {t('admin.form.locationEs')}
                      </label>
                      <input
                        type="text"
                        name="localizacao_es"
                        value={formData.localizacao_es}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                {/* Outros campos */}
                <div className="grid grid-cols-2 gap-4 border-t border-gray-200 pt-4">
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      {t('admin.form.duration')}
                    </label>
                    <input
                      type="text"
                      name="duracao"
                      value={formData.duracao}
                      onChange={handleChange}
                      placeholder="Ex: 2 horas"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Imagens Existentes */}
                {imagensExistentes.length > 0 && (
                  <div className="border-t border-gray-200 pt-4">
                    <label className="block text-gray-700 font-semibold mb-2">
                      {t('admin.form.currentImages')}
                    </label>
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      {imagensExistentes.map((imagem) => (
                        <div key={imagem.id} className="relative group">
                          <img
                            src={getImageUrl(imagem.imagem_url)}
                            alt="Imagem do passeio"
                            className="w-full h-24 object-cover object-center rounded border border-gray-300"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveExistingImage(imagem.id)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upload de Novas Imagens */}
                <div className="border-t border-gray-200 pt-4">
                  <label className="block text-gray-700 font-semibold mb-2">
                    {t('admin.form.addImages')}
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-gray-500 text-sm mt-1">
                    {t('admin.form.maxFileSize') || 'Tamanho máximo por arquivo: 2MB'}
                    Você pode selecionar múltiplas imagens (máximo 10)
                  </p>
                  {imagens.length > 0 && (
                    <div className="mt-3 grid grid-cols-3 gap-2">
                      {imagens.map((file, index) => (
                        <div key={index} className="relative">
                          <img
                            src={URL.createObjectURL(file)}
                            alt="Preview"
                            className="w-full h-24 object-cover object-center rounded border border-gray-300"
                          />
                          <button
                            type="button"
                            onClick={() => setImagens(imagens.filter((_, i) => i !== index))}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <label className="block text-gray-700 font-semibold mb-2">
                    {t('admin.form.whatsapp')}
                  </label>
                  <input
                    type="text"
                    name="whatsapp"
                    value={formData.whatsapp}
                    onChange={handleChange}
                    placeholder="5511999999999"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-gray-500 text-sm mt-1">
                    Se vazio, será usado o número WhatsApp global
                  </p>
                </div>

                <div className="flex items-center border-t border-gray-200 pt-4">
                  <input
                    type="checkbox"
                    name="disponivel"
                    checked={formData.disponivel}
                    onChange={handleChange}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label className="ml-2 text-gray-700 font-semibold">
                    {t('admin.form.available')}
                  </label>
                </div>

                <div className="flex gap-4 pt-4 border-t border-gray-200">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {saving ? (
                      <>
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>{editingPasseio ? 'Salvando...' : 'Criando...'}</span>
                      </>
                    ) : (
                      <span>{editingPasseio ? t('admin.save') : t('admin.addTour')}</span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    disabled={saving}
                    className="flex-1 bg-gray-300 text-gray-800 py-3 rounded-lg font-semibold hover:bg-gray-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('admin.cancel')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className="fixed top-4 right-4 z-[100]">
          <Toast
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            onClose={() => setToast(null)}
          />
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;

